<template>
    <FetchTemplatesByPage
        v-slot="{
            pageSize,
            results: templates,
            isLoading,
            isFullyLoaded,
            fetchPage,
        }"
        :query="query"
        :variables="variables"
        :page-size="25"
        @error="handleError"
    >
        <TemplateSelectionPanelActions
            v-slot="{
                handleFromScratchTemplateSelected,
                handleFromPublicTemplateSelected,
                handleFromSimpleTextTemplateSelected,
                creatingTemplateId,
                handleGalleryTemplatePreview,
                closeTemplatePreview,
                previewTemplate,
                previewTemplateHtml,
                isLoadingPreviewSelection,
                isLoadingPreview,
                isCreating,
            }"
            :marketing-email-id="marketingEmailId"
            :source="source"
            @template-selected="$emit('template-selected', $event)"
            @template-created="$emit('template-created', $event)"
        >
            <div>
                <TemplateGalleryCardsLayout
                    :error="error"
                    :is-fully-loaded="isFullyLoaded"
                    :is-loading="isLoading"
                    :items="templates"
                    :page-size="pageSize"
                    @fetch="fetchPage"
                >
                    <template #filters>
                        <TemplateGalleryFilters
                            :industry.sync="industry"
                            :usage.sync="usage"
                        />
                    </template>

                    <template #static>
                        <StartFromScratchCard
                            :loading="creatingTemplateId === 'scratch'"
                            :disabled="isCreating"
                            @select="$emit('attempt-to-select-template', { callback: handleFromScratchTemplateSelected, type: 'scratch', template: {} })"
                        />

                        <SimpleTextCard
                            :loading="creatingTemplateId === 'simple-text'"
                            :disabled="isCreating"
                            @select="$emit('attempt-to-select-template', { callback: handleFromSimpleTextTemplateSelected, type: 'simple-text', template: {} })"
                        />
                    </template>

                    <template #card="{ item: template }">
                        <TemplateGalleryCard
                            :key="template.id"
                            :template="template"
                            :selected="creatingTemplateId === template.id"
                            :disabled="isCreating"
                            :is-loading-preview="isLoadingPreview"
                            :show-header="false"
                            :preview-enabled="previewEnabled"
                            @click="$emit('attempt-to-select-template', { callback: handleFromPublicTemplateSelected, type: 'public', template })"
                            @preview="handleGalleryTemplatePreview(template)"
                        />
                    </template>

                    <template #empty>
                        <div class="no-results-container">
                            <div class="no-results-message">
                                {{ $t('unlayerBuilder.templateSelection.noSearchResults') }}
                            </div>
                            <DsButton 
                                :loading="isCreating"
                                @click="$emit('attempt-to-select-template', { callback: handleFromScratchTemplateSelected, type: 'scratch', template: {} })"
                            >
                                {{ $t('email.broadcast.unlayer.template.selection.common.startFromScratch') }}
                            </DsButton>
                        </div>
                    </template>
                </TemplateGalleryCardsLayout>

                <ContentPreviewModal
                    v-if="previewEnabled"
                    data-qa="campaign-email-template-preview-modal"
                    :preview-html="previewTemplateHtml"
                    @close="closeTemplatePreview"
                >
                    <DsFilledButton
                        data-qa="use-this-template"
                        :loading="isLoadingPreviewSelection"
                        @click="$emit('attempt-to-select-template', { callback: handleFromPublicTemplateSelected, type: 'public', template: previewTemplate })"
                    >
                        Use this template
                    </DsFilledButton>
                </ContentPreviewModal>
            </div>
        </TemplateSelectionPanelActions>
    </FetchTemplatesByPage>
</template>

<script>
import FetchTemplatesByPage from './FetchTemplatesByPage';
import TemplateSelectionPanelActions from './TemplateSelectionPanelActions';
import TemplateGalleryCardsLayout from './TemplateGalleryCardsLayout';
import TemplateGalleryFilters from './TemplateGalleryFilters';
import TemplateGalleryCard from './TemplateGalleryCard';
import ContentPreviewModal from './ContentPreviewModal';
import StartFromScratchCard from './StartFromScratchCard';
import SimpleTextCard from './SimpleTextCard';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';
import { UNLAYER_EMAIL_FUNNEL_BASE_URL } from '@/api/campaigns/campaignUnlayerEmail';
import { UNLAYER_BROADCAST_BASE_URL } from '@/api/broadcasts/emailBroadcast';

const ERROR_MESSAGE = 'For some reason your gallery didn’t load. Try refreshing the page.';
const CLOSE_LABEL = 'Ok, got it';

export default {
    name: 'TemplateSelectionGalleryPanel',

    components: {
        FetchTemplatesByPage,
        TemplateSelectionPanelActions,
        TemplateGalleryCardsLayout,
        TemplateGalleryFilters,
        TemplateGalleryCard,
        ContentPreviewModal,
        StartFromScratchCard,
        SimpleTextCard,
    },

    props: {
        marketingEmailId: String,
        previewEnabled: Boolean,
        source: {
            type: String,
            default: SOURCES.CAMPAIGN,
        },
    },

    data() {
        return {
            error: null,
            industry: null,
            usage: null,
        };
    },

    watch: {
        variables() {
            this.error = null;
        },
    },

    computed: {
        query() {
            let url = '';

            if (this.source === SOURCES.BROADCAST) {
                url = `${UNLAYER_BROADCAST_BASE_URL}/globalTemplates`;
            } else if (this.source === SOURCES.CAMPAIGN) {
                url = `${UNLAYER_EMAIL_FUNNEL_BASE_URL}/globalTemplates`;
            }

            return {
                url,
                method: 'get',
            };
        },

        variables() {
            return {
                industry: this.industry,
                usage: this.usage,
            };
        },
    },

    methods: {
        handleError(error) {
            this.error = error;
            this.$error({
                bottom: true,
                closeLabel: CLOSE_LABEL,
                message: ERROR_MESSAGE,
                showIcon: true,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.no-results-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: $spacing-800;
  gap: $spacing-400;
}

.no-results-message {
  font-size:$font-size-md;
  font-weight: $font-weight-regular;
  text-align: center;
}
</style>
